import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, ArrowRight, Clock, Users, Anchor, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { useDeposit } from "../context/DepositContext";

export const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShow(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

export const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-[#f5a524] font-heading tracking-[0.25em] uppercase text-xs mb-3">
    <span className="h-px w-8 bg-[#f5a524]" /> {children}
  </span>
);

export const SectionTitle = ({ eyebrow, title, sub, light = false, center = false }) => (
  <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-3xl"} mb-10`}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className={`font-display text-4xl md:text-5xl leading-[1.05] ${light ? "text-[#0a1f33]" : "text-white"}`}>{title}</h2>
    {sub && <p className={`mt-4 text-base md:text-lg ${light ? "text-slate-600" : "text-slate-300"}`}>{sub}</p>}
  </div>
);

export const PageHero = ({ image, eyebrow, title, sub, tall = false }) => (
  <section className={`relative ${tall ? "min-h-[62vh]" : "min-h-[46vh]"} flex items-center overflow-hidden`}>
    <div className="absolute inset-0">
      <img src={image} alt={title} className="h-full w-full object-cover animate-kenburns" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f33] via-[#0a1f33]/60 to-[#0a1f33]/30" />
    </div>
    <div className="container-x relative z-10 pt-20 pb-14">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className="font-display text-4xl md:text-6xl text-white leading-[1.02] text-shadow-hero max-w-4xl">{title}</h1>
      {sub && <p className="mt-4 text-slate-200 text-lg max-w-2xl">{sub}</p>}
    </div>
  </section>
);

export const StarRow = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-[#f5a524] text-[#f5a524]" />
    ))}
  </div>
);

export const TripCard = ({ trip }) => {
  const { t } = useTranslation();
  const { openDeposit } = useDeposit();
  return (
    <div className="card-hover group rounded-2xl overflow-hidden bg-[#0e2740] border border-white/10 flex flex-col">
      <div className="relative h-56 overflow-hidden">
        <img src={trip.image} alt={trip.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e2740] to-transparent" />
        <span className="absolute top-3 right-3 rounded-full bg-[#f5a524] px-3 py-1 text-sm font-bold text-[#0a1f33]">${trip.price}{trip.priceFull ? `+` : ""}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[#f5a524] text-xs font-heading tracking-widest uppercase">{trip.hook}</p>
        <h3 className="font-heading text-2xl text-white mt-1">{trip.name}</h3>
        <div className="flex gap-4 text-xs text-slate-400 mt-2">
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {trip.duration}</span>
          <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {trip.capacity}</span>
        </div>
        <p className="text-sm text-slate-300 mt-3 flex-1">{trip.short}</p>
        <div className="flex gap-2 mt-5">
          <Link to={`/trips/${trip.slug}`} className="flex-1">
            <Button variant="outline" className="w-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">{t("common.details")}</Button>
          </Link>
          <Button className="btn-gold flex-1" onClick={() => openDeposit(trip)}>{t("common.book")}</Button>
        </div>
      </div>
    </div>
  );
};

export const SpeciesCard = ({ s }) => {
  const { t } = useTranslation();
  return (
  <Link to={`/species/${s.slug}`} className="card-hover group relative rounded-2xl overflow-hidden block h-72 border border-white/10">
    <img src={s.image} alt={s.name} className="absolute inset-0 h-full w-full object-cover object-center transition duration-500 group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f33] via-[#0a1f33]/40 to-transparent" />
    <span className={`absolute top-3 left-3 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${s.zone === "offshore" ? "bg-[#1c6ba3] text-white" : "bg-[#0e9488] text-white"}`}>{t(`plan.${s.zone}`)}</span>
    <div className="absolute bottom-0 p-4">
      <h3 className="font-heading text-2xl text-white">{s.name}</h3>
      <p className="text-xs italic text-slate-300">{s.latin}</p>
      <p className="text-xs text-[#f5a524] mt-1 flex items-center gap-1">{t("common.peak")} {s.peak} <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition" /></p>
    </div>
  </Link>
  );
};

export const CTASection = ({ image }) => {
  const { t } = useTranslation();
  const { openDeposit } = useDeposit();
  return (
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0a1f33]/80" />
      </div>
      <div className="container-x relative z-10 text-center">
        <Eyebrow>{t("cta.eyebrow")}</Eyebrow>
        <h2 className="font-display text-4xl md:text-6xl text-white max-w-3xl mx-auto leading-[1.05]">{t("cta.title")}</h2>
        <p className="text-slate-200 mt-4 max-w-xl mx-auto">{t("cta.sub")}</p>
        <div className="flex flex-wrap gap-3 justify-center mt-7">
          <Link to="/plan-your-trip"><Button className="btn-gold h-12 px-7 text-base gap-2"><Anchor className="h-5 w-5" /> {t("common.planYourTrip")}</Button></Link>
          <Button variant="outline" className="h-12 px-7 text-base border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white gap-2" onClick={() => openDeposit()}><MessageCircle className="h-5 w-5" /> {t("common.bookWhatsApp")}</Button>
        </div>
      </div>
    </section>
  );
};
